import React, { useState, useMemo } from 'react'

import { Tabs, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, string } from 'prop-types'

import CustomSelect from 'Pods/Shared/CustomSelect/CustomSelect'
import Sidebar from 'Pods/Shared/Sidebar/Sidebar'

import RegularEarningsDownloadsTab from './RegularEarningsDownloadsTab'
import RegularEarningsSalesTab from './RegularEarningsSalesTab'
import { formatMoneyValue } from '../../../formatter'
import useAvailableMonths from '../../../Hooks/useAvailableMonths'
import { REGULAR_EARNINGS_PANEL_CONFIG } from '../../../Static/earningsDetailConfig'
import { ALL_MONTHS_OPTION, ALL_MONTHS_VALUE, sortedDescending, ymToOption } from '../../../Static/monthOptions'

const TABS = [
  REGULAR_EARNINGS_PANEL_CONFIG.tabs.sales,
  REGULAR_EARNINGS_PANEL_CONFIG.tabs.downloads,
]

const DEFAULT_TAB_ID = REGULAR_EARNINGS_PANEL_CONFIG.tabs.sales.id
const SEARCHABLE_THRESHOLD = 12

function RegularEarningsDetailPanel({
  isOpen,
  onClose,
  thisMonthAmount = 0,
  initialMonth = null,
}) {
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB_ID)
  // initialMonth seeds state only on mount; the panel is unmounted on close, so each open re-evaluates this
  const [monthOption, setMonthOption] = useState(() => (
    initialMonth ? ymToOption(initialMonth) : ALL_MONTHS_OPTION
  ))

  const {
    sales: availableSales,
    downloads: availableDownloads,
    isLoading: isLoadingMonths,
  } = useAvailableMonths({ enabled: isOpen })

  const isSalesActive = activeTab === REGULAR_EARNINGS_PANEL_CONFIG.tabs.sales.id
  const activeMonths = isSalesActive ? availableSales : availableDownloads

  const monthOptions = useMemo(() => {
    const set = new Set(activeMonths)
    if (monthOption.value !== ALL_MONTHS_VALUE) set.add(monthOption.value)
    return [ALL_MONTHS_OPTION, ...sortedDescending([...set]).map(ymToOption)]
  }, [activeMonths, monthOption.value])

  const selectedMonth = monthOption.value === ALL_MONTHS_VALUE ? null : monthOption.value
  const isSearchable = monthOptions.length > SEARCHABLE_THRESHOLD

  const handleMonthChange = (option) => {
    setMonthOption(option || ALL_MONTHS_OPTION)
  }

  const handleTabChange = (nextTabId) => {
    const nextIsSalesActive = nextTabId === REGULAR_EARNINGS_PANEL_CONFIG.tabs.sales.id
    const nextMonths = nextIsSalesActive ? availableSales : availableDownloads

    if (monthOption.value !== ALL_MONTHS_VALUE && !nextMonths.includes(monthOption.value)) {
      setMonthOption(ALL_MONTHS_OPTION)
    }
    setActiveTab(nextTabId)
  }

  return (
    <Sidebar
      isOpen={isOpen}
      closeSidebar={onClose}
      title={REGULAR_EARNINGS_PANEL_CONFIG.title}
      className="regular-earnings-detail-panel"
    >
      <div className="regular-earnings-detail-panel__header">
        <span className="regular-earnings-detail-panel__subtitle cgt-body-base">
          {`$${formatMoneyValue(thisMonthAmount)} this month`}
        </span>
        <CustomSelect
          className="regular-earnings-detail-panel__month-select overview-select"
          valueClassName="cgt-body-base-semibold"
          options={monthOptions}
          value={monthOption}
          onChange={handleMonthChange}
          ariaLabel="Filter by month"
          isLoading={isLoadingMonths}
          searchable={isSearchable}
        />
      </div>

      <div className="regular-earnings-detail-panel__tabs">
        <Tabs
          activeTabId={activeTab}
          onClick={handleTabChange}
          tabs={TABS}
        />
      </div>

      {/* Both tabs are always mounted so switching between them preserves
          in-tab state (column visibility, pagination) */}
      <div
        className={classnames(
          'regular-earnings-detail-panel__tab-panel',
          !isSalesActive && 'regular-earnings-detail-panel__tab-panel--hidden'
        )}
      >
        <RegularEarningsSalesTab
          isActive={isOpen && isSalesActive}
          selectedMonth={selectedMonth}
        />
      </div>
      <div
        className={classnames(
          'regular-earnings-detail-panel__tab-panel',
          isSalesActive && 'regular-earnings-detail-panel__tab-panel--hidden'
        )}
      >
        <RegularEarningsDownloadsTab
          isActive={isOpen && !isSalesActive}
          selectedMonth={selectedMonth}
        />
      </div>
    </Sidebar>
  )
}

RegularEarningsDetailPanel.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  thisMonthAmount: number,
  initialMonth: string,
}

export default RegularEarningsDetailPanel
